/**
 * useNodeSettings - Hook for managing node settings state
 *
 * Provides:
 * - Settings state management for a selected node
 * - Integration with operation manifest for settings availability
 * - Settings persistence back to workflow definition
 *
 * Uses the manifest-driven approach to determine which settings are supported.
 * For operations without explicit manifests, provides sensible defaults based on node type.
 */

import { useCallback, useMemo } from 'react';
import { useOperationManifestData } from './useOperationManifest';
import { useWorkflowStore } from '@/stores/workflowStore';
import { extractSettingsFromNode } from '@/stores/nodeEditorStore';
import type { NodeSettings, SettingData } from '@/types/nodeSettings';
import { DEFAULT_RETRY_POLICY, DEFAULT_CONCURRENCY } from '@/types/nodeSettings';
import type { OperationInfo } from '@/types/operationManifest';
import type { Action, Trigger } from '@/types/workflowDefinition';

/**
 * Hook return value
 */
export interface UseNodeSettingsReturn {
  /** Current settings from the manifest */
  settings: NodeSettings | null;
  /** Whether the node is a trigger */
  isTrigger: boolean;
  /** Whether settings are loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Available predecessor actions for run-after configuration */
  availablePredecessors: string[];
  /** Update action settings in the workflow store */
  updateActionSettings: (actionName: string, updates: Partial<Action>) => void;
}

/**
 * Create default settings for a node based on its type
 * This provides sensible defaults when no manifest is available
 *
 * Based on LogicAppsUX patterns:
 * - All actions support: timeout, trackedProperties, secureData
 * - Retry policy is only supported for: apiconnection, apiconnectionwebhook, http types
 * - All actions (non-triggers) support: runAfter
 * - Triggers support: concurrency, splitOn, conditionExpressions
 * - Foreach actions support: concurrency
 */
function createDefaultSettings(isTrigger: boolean, nodeType?: string): NodeSettings {
  // Helper to create a supported setting with optional value
  // Uses spread to conditionally include value (handles exactOptionalPropertyTypes)
  const createSupported = <T>(value?: T): SettingData<T> => {
    if (value !== undefined) {
      return { isSupported: true, value };
    }
    return { isSupported: true } as SettingData<T>;
  };

  const createUnsupported = <T>(): SettingData<T> => ({
    isSupported: false,
  });

  // Retry policy is only supported for types that make external calls,
  // matching LogicAppsUX behavior (apiconnection, apiconnectionwebhook, http)
  const retryableTypes = ['http', 'apiconnection', 'apiconnectionwebhook'];
  const isRetryable = nodeType
    ? retryableTypes.includes(nodeType.toLowerCase())
    : false;

  // Base settings available to all operations
  const baseSettings: NodeSettings = {
    // General - available to all
    timeout: createSupported<string>(),

    // Networking - retry only for retryable types
    retryPolicy: isRetryable
      ? createSupported({ ...DEFAULT_RETRY_POLICY })
      : createUnsupported(),

    // Security - available to all
    secureInputs: createSupported<boolean>(),
    secureOutputs: createSupported<boolean>(),

    // Tracking - available to all actions
    trackedProperties: isTrigger ? createUnsupported() : createSupported<Record<string, string>>(),
  };

  const normalizedNodeType = nodeType?.toLowerCase();
  const supportsForeachConcurrency = normalizedNodeType === 'foreach' || normalizedNodeType === 'for_each';

  if (isTrigger) {
    // Trigger-specific settings
    return {
      ...baseSettings,
      concurrency: createSupported({ ...DEFAULT_CONCURRENCY }),
      splitOn: createSupported(),
      conditionExpressions: createSupported<string[]>(),
      runAfter: createUnsupported(),
    };
  } else {
    // Action-specific settings
    return {
      ...baseSettings,
      concurrency: supportsForeachConcurrency
        ? createSupported({ ...DEFAULT_CONCURRENCY })
        : createUnsupported(),
      splitOn: createUnsupported(),
      conditionExpressions: createUnsupported(),
      runAfter: createSupported(),
      // Loop/Agent-specific settings (for Foreach/Until/Agent)
      // Agent actions have limit.count and limit.timeout for iteration and time limits
      count: normalizedNodeType === 'foreach' || normalizedNodeType === 'for_each'
          || normalizedNodeType === 'until' || normalizedNodeType === 'agent'
        ? createSupported<string | number>()
        : createUnsupported(),
    };
  }
}

/**
 * Check if a value is a SettingData object (has isSupported property)
 */
function isSettingData(value: unknown): value is SettingData<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'isSupported' in value &&
    typeof (value as Record<string, unknown>).isSupported === 'boolean'
  );
}

/**
 * Merge extracted settings with defaults
 * Extracted settings (from workflow) take precedence for values,
 * defaults provide isSupported flags and initial values
 */
function mergeSettingsWithDefaults(
  defaults: NodeSettings,
  extracted: Partial<NodeSettings>
): NodeSettings {
  const merged: NodeSettings = { ...defaults };

  // For each setting in defaults, check if we have an extracted value
  (Object.keys(defaults) as Array<keyof NodeSettings>).forEach((key) => {
    const defaultSetting = defaults[key];
    const extractedSetting = extracted[key];

    // Only merge SettingData types (have isSupported property)
    if (isSettingData(defaultSetting) && defaultSetting.isSupported && isSettingData(extractedSetting)) {
      // Merge: keep isSupported from default, use value from extracted
      (merged as Record<string, unknown>)[key] = {
        ...defaultSetting,
        value: extractedSetting.value,
      };
    }
  });

  return merged;
}

/**
 * Hook for managing node settings
 *
 * @param nodeId - The node ID (e.g., 'action-HTTP' or 'trigger-manual')
 * @param operationInfo - Operation info for manifest lookup
 * @param nodeType - The node type (e.g., 'Agent', 'Http', 'Foreach') for default settings
 * @param nodeData - Optional node data to extract existing settings from
 * @returns Settings state and handlers
 */
export function useNodeSettings(
  nodeId: string | null,
  operationInfo: OperationInfo | null,
  nodeType?: string,
  nodeData?: Action | Trigger | null
): UseNodeSettingsReturn {
  // Get workflow store
  const workflow = useWorkflowStore((state) => state.workflow);
  const updateAction = useWorkflowStore((state) => state.updateAction);
  const getAction = useWorkflowStore((state) => state.getAction);

  // Get manifest data and settings availability
  const {
    settings: manifestSettings,
    isTrigger: manifestIsTrigger,
    isLoading,
    error: manifestError,
  } = useOperationManifestData(operationInfo);

  // Determine if this node is a trigger
  const isNodeTrigger = useMemo(() => {
    if (!nodeId) return false;
    return nodeId.startsWith('trigger-');
  }, [nodeId]);

  // Use manifest settings if available, otherwise use defaults based on node type
  // Then merge with any existing settings from the workflow definition
  const settings = useMemo<NodeSettings | null>(() => {
    // If no nodeId, we can't determine defaults
    if (!nodeId || isLoading) {
      return null;
    }

    // Start with manifest settings or defaults
    const baseSettings = manifestSettings ?? createDefaultSettings(isNodeTrigger, nodeType);

    // If we have node data, extract existing settings and merge
    if (nodeData) {
      const extractedSettings = extractSettingsFromNode(nodeData, isNodeTrigger);
      return mergeSettingsWithDefaults(baseSettings, extractedSettings);
    }

    return baseSettings;
  }, [manifestSettings, nodeId, isNodeTrigger, nodeType, nodeData, isLoading]);

  // Use manifest isTrigger or fall back to nodeId-based detection
  const isTrigger = manifestIsTrigger || isNodeTrigger;

  // Get available predecessor actions for run-after (with cycle prevention)
  const availablePredecessors = useMemo(() => {
    if (!workflow || isNodeTrigger) return [];

    const nodeName = nodeId?.replace(/^(trigger-|action-)/, '');
    if (!nodeName) return [];

    const actions = workflow.definition.actions;
    const allNames = Object.keys(actions);

    // BFS to find all successor actions (actions that directly or transitively
    // depend on this node). Adding any of these as a runAfter dependency would
    // create a cycle.
    const successors = new Set<string>();
    const queue = [nodeName];
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const name of allNames) {
        if (successors.has(name)) continue;
        const runAfter = actions[name]?.runAfter;
        if (runAfter && current in runAfter) {
          successors.add(name);
          queue.push(name);
        }
      }
    }

    // Exclude: self, successor nodes (cycle prevention)
    return allNames.filter(
      (name) => name !== nodeName && !successors.has(name)
    );
  }, [workflow, nodeId, isNodeTrigger]);

  // Handler to update action settings in the workflow
  const updateActionSettings = useCallback(
    (actionName: string, updates: Partial<Action>) => {
      const currentAction = getAction(actionName);
      if (!currentAction) return;

      updateAction(actionName, {
        ...currentAction,
        ...updates,
      });
    },
    [getAction, updateAction]
  );

  // Convert manifest error to string
  const error = manifestError ? String(manifestError) : null;

  return {
    settings,
    isTrigger,
    isLoading,
    error,
    availablePredecessors,
    updateActionSettings,
  };
}

/**
 * Hook for getting action data from the workflow definition
 * Useful for getting the raw action/trigger definition
 */
export function useActionFromWorkflow(nodeId: string | null) {
  const workflow = useWorkflowStore((state) => state.workflow);
  const getAction = useWorkflowStore((state) => state.getAction);
  const getTrigger = useWorkflowStore((state) => state.getTrigger);

  return useMemo(() => {
    if (!nodeId || !workflow) return null;

    // Parse the node ID
    const isTrigger = nodeId.startsWith('trigger-');
    const nodeName = nodeId.replace(/^(trigger-|action-)/, '');

    if (isTrigger) {
      return { type: 'trigger' as const, data: getTrigger(nodeName) ?? null };
    } else {
      return { type: 'action' as const, data: getAction(nodeName) ?? null };
    }
  }, [nodeId, workflow, getAction, getTrigger]);
}

export default useNodeSettings;
